import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Bell, MessageCircle, Calendar, ClipboardCheck, Check, Trash2, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeConfig = {
  chat: { icon: MessageCircle, color: 'text-purple-400', bg: 'bg-purple-500/10' },
  appointment: { icon: Calendar, color: 'text-green-400', bg: 'bg-green-500/10' },
  checkin: { icon: ClipboardCheck, color: 'text-orange-400', bg: 'bg-orange-500/10' },
  default: { icon: Bell, color: 'text-[#0094ff]', bg: 'bg-[#0094ff]/10' },
};

export const NotificationBell = () => {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  // Tracks IDs of notifications we've already turned into a desktop popup,
  // so the same notification doesn't fire again on every polling tick.
  const seenIdsRef = useRef(new Set());
  const initializedRef = useRef(false);

  const fireDesktopNotification = useCallback((notif) => {
    if (typeof window === 'undefined') return;
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    try {
      const n = new Notification(notif.title || 'Neue Nachricht', {
        body: notif.message || '',
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: `ow-${notif.id}`,
        renotify: true,
      });
      n.onclick = () => {
        try { window.focus(); } catch (_) { /* noop */ }
        if (notif.link) navigate(notif.link);
        n.close();
      };
    } catch (_) { /* swallow — some browsers reject when tab hidden */ }
  }, [navigate]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      // Wir holen direkt die Liste — sie liefert den count und ermöglicht uns,
      // pro neuer Notification eine konkrete Desktop-Notification mit Inhalt
      // ("Neue Nachricht von Anna: …") auszulösen statt eines generischen Pings.
      const res = await axios.get(`${API_URL}/api/notifications`);
      const items = Array.isArray(res.data) ? res.data : [];
      const unread = items.filter(n => !n.read);
      setUnreadCount(unread.length);
      setNotifications(items);

      // Beim ersten Tick nur die existierenden IDs als „gesehen" merken,
      // damit wir bei einem Page-Reload nicht alle alten Notifications spammen.
      if (!initializedRef.current) {
        items.forEach(n => seenIdsRef.current.add(n.id));
        initializedRef.current = true;
        return;
      }
      // Neu hinzugekommene unread-Notifications popup'en
      for (const n of unread) {
        if (!seenIdsRef.current.has(n.id)) {
          fireDesktopNotification(n);
          seenIdsRef.current.add(n.id);
        }
      }
    } catch (e) { /* silent */ }
  }, [fireDesktopNotification]);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/notifications`);
      setNotifications(res.data);
    } catch (e) { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 8000);
    const onVisibility = () => { if (!document.hidden) fetchUnreadCount(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthenticated, fetchUnreadCount]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read-all`);
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) { /* silent */ }
  };

  const handleClearAll = async () => {
    try {
      await axios.delete(`${API_URL}/api/notifications`);
      setNotifications([]);
      setUnreadCount(0);
    } catch (e) { /* silent */ }
  };

  const handleClick = async (notification) => {
    if (!notification.read) {
      try {
        await axios.put(`${API_URL}/api/notifications/${notification.id}/read`);
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, read: true } : n));
      } catch (e) { /* silent */ }
    }
    if (notification.link) {
      navigate(notification.link);
      setOpen(false);
    }
  };

  const requestPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => { setOpen(!open); requestPermission(); }}
        className="relative p-2 text-white/80 hover:text-[#0094ff] transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-12 w-80 sm:w-96 bg-[#1a1717] border border-[#605e5e]/30 rounded-lg shadow-2xl overflow-hidden z-popover"
            data-testid="notification-dropdown"
          >
            <div className="flex items-center justify-between p-4 border-b border-[#605e5e]/20">
              <h3 className="text-white font-semibold text-sm">Benachrichtigungen</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button onClick={handleMarkAllRead} className="text-[#0094ff] text-xs hover:underline">
                    Alle gelesen
                  </button>
                )}
                {notifications.length > 0 && (
                  <button onClick={handleClearAll} className="text-red-400 hover:text-red-300">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {loading ? (
                <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-[#0094ff] border-t-transparent rounded-full animate-spin" /></div>
              ) : notifications.length === 0 ? (
                <div className="py-8 text-center text-[#a1a1aa] text-sm">
                  Keine Benachrichtigungen
                </div>
              ) : (
                notifications.map((n) => {
                  const config = typeConfig[n.type] || typeConfig.default;
                  const Icon = config.icon;
                  return (
                    <button
                      key={n.id}
                      onClick={() => handleClick(n)}
                      className={`w-full text-left p-4 border-b border-[#605e5e]/10 hover:bg-white/5 transition-colors flex gap-3 ${!n.read ? 'bg-[#0094ff]/5' : ''}`}
                    >
                      <div className={`w-9 h-9 ${config.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-4 h-4 ${config.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${!n.read ? 'text-white' : 'text-[#a1a1aa]'}`}>{n.title}</p>
                        <p className="text-xs text-[#605e5e] mt-0.5 truncate">{n.message}</p>
                        <p className="text-[10px] text-[#605e5e] mt-1">
                          {new Date(n.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      {!n.read && <div className="w-2 h-2 bg-[#0094ff] rounded-full flex-shrink-0 mt-2" />}
                    </button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
